import { ReactNode } from 'react';
import { type CSSObject } from '@emotion/react';
import { Person } from '@weave/schema-gen-ts/dist/schemas/persons/v3/persons.pb';
import { PersonsV3 } from '@frontend/api-person';
import { useTranslation } from '@frontend/i18n';
import { Icon } from '@frontend/icons';
import { getFullName } from '@frontend/name';
import { useThreadHeaderContext, type ThreadHeaderSize } from '@frontend/thread-header-context';
import { CommonHTMLButtonAttributes } from '@frontend/types';
import { theme } from '@frontend/theme';
import { NakedButton, type TextProps } from '@frontend/design-system';
import { ContactRowSkeleton } from './contact-row-skeleton';
import { PersonSecondaryInfo } from './person-secondary-info';
import { ThreadTitle } from './thread-title';

export type ContactListItemVariant = 'standard' | 'group';

type ContactListItemProps = {
  personId: string;
  person?: Person;
  locationId: string;
  size?: ThreadHeaderSize;
  variant?: ContactListItemVariant;
  disableInteraction?: boolean;
  showSecondaryInfo?: boolean;
  nameAreaSlot?: ReactNode;
  rightSlot?: ReactNode;
  children?: ReactNode;
  isPrimary?: boolean;
  trackingId?: string;
  /** When true, contact name text renders with subdued color */
  isOptedOut?: boolean;
  /** When true, contact name text renders with subdued color (blocked) */
  isBlocked?: boolean;
} & CommonHTMLButtonAttributes;

export const ContactListItem = ({
  personId,
  person: providedPerson,
  locationId,
  size = 'normal',
  variant = 'standard',
  disableInteraction = false,
  showSecondaryInfo = false,
  nameAreaSlot,
  rightSlot,
  children,
  isPrimary = false,
  isOptedOut = false,
  isBlocked = false,
  ...rest
}: ContactListItemProps) => {
  const { t } = useTranslation('thread-header');

  const { associatedContacts } = useThreadHeaderContext();
  const { data: fetchedPerson, isLoading } = PersonsV3.PersonQueries.useGetPersonLegacyQuery(
    {
      personId,
      locationIds: [locationId],
    },
    {
      enabled: !providedPerson && !!personId && !!locationId,
    }
  );
  const person = providedPerson || fetchedPerson;

  const getHeadOfHousehold = (contacts: Person[]): string | undefined => {
    if (!Array.isArray(contacts)) return undefined;

    const filteredContacts = contacts.filter(
      (contact): contact is Person & { birthdate: string } =>
        contact.isGuardian === true && !!contact.birthdate && contact.personPmid === contact.householdPmid
    );

    if (filteredContacts.length === 0) {
      return undefined;
    }

    const headOfHousehold = filteredContacts.reduce((oldest, current) =>
      new Date(current.birthdate) < new Date(oldest.birthdate) ? current : oldest
    );

    return headOfHousehold.personId;
  };

  const headOfHouseholdId = getHeadOfHousehold(associatedContacts || []);

  const variantStyles: Record<
    ContactListItemVariant,
    {
      nameTextSize?: TextProps['size'];
      subtextSize?: TextProps['size'];
      secondaryInfoCss?: CSSObject;
      itemCss?: CSSObject;
    }
  > = {
    standard: {},
    group: {
      nameTextSize: 'medium',
      subtextSize: 'small',
      secondaryInfoCss: {
        marginLeft: 'var(--fabric-number-n-1-px)',
      },
      itemCss: {
        height: 'auto',
        paddingTop: 'var(--fabric-number-n-1-5-px)',
        paddingBottom: 'var(--fabric-number-n-1-5-px)',
        textAlign: 'left',
      },
    },
  };

  const { nameTextSize, subtextSize, secondaryInfoCss, itemCss } = variantStyles[variant];

  if (isLoading) {
    return <ContactRowSkeleton showSubtext={showSecondaryInfo} />;
  }

  if (!person) return null;

  return (
    <NakedButton
      data-value={personId}
      css={[
        {
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          cursor: disableInteraction ? 'default' : 'pointer',
          padding: size === 'normal' ? theme.spacing(1, 2) : theme.spacing(0.5, 1),
          gap: theme.spacing(1),
          width: '100%',
          maxWidth: '100%',
          height: 40,
          borderBottom: `1px solid ${theme.colors.neutral10}`,
        },
        !disableInteraction && {
          ':hover': {
            backgroundColor: theme.colors.neutral5,
          },
        },
        !disableInteraction && {
          // Make children icon buttons have correct background color on hover
          button: {
            ':hover:not(&[aria-disabled="true"]), :focus': {
              backgroundColor: theme.colors.white,
            },
          },
        },
        itemCss,
      ]}
      {...rest}
      onClick={(e) => {
        rest.onClick?.(e);
      }}
    >
      <div
        css={{
          display: 'flex',
          alignItems: 'center',
          gap: theme.spacing(1),
          minWidth: 0,
          paddingLeft: isPrimary ? 0 : theme.spacing(size === 'small' ? 1 : 2),
        }}
      >
        {!isPrimary && <Icon name='nested' size={16} color='disabled' css={{ marginBottom: theme.spacing(1) }} />}
        <ThreadTitle
          text={getFullName(person)}
          subtext={isPrimary ? t('Phone Owner') : undefined}
          subtextSize={subtextSize}
          avatarProps={{
            size: 'small',
          }}
          personId={personId}
          textProps={{
            type: 'text',
            size: nameTextSize ?? (size === 'normal' ? undefined : 'small'),
            color: isOptedOut || isBlocked ? 'subdued' : undefined,
          }}
          openContactPanel={false}
          headOfHouseholdId={associatedContacts && associatedContacts?.length > 1 ? headOfHouseholdId : undefined}
        >
          {nameAreaSlot}
          {(size !== 'small' || showSecondaryInfo) && (
            <PersonSecondaryInfo
              personId={personId}
              person={person}
              css={[
                {
                  padding: theme.spacing(0, 1),
                  borderLeft: `1px solid ${theme.colors.neutral20}`,
                },
                secondaryInfoCss,
              ]}
              size={size}
            />
          )}
        </ThreadTitle>
      </div>
      <div css={{ display: 'flex', alignItems: 'center', gap: 'var(--fabric-number-n-2-px)' }}>
        {rightSlot && (
          /**
           * On clicking the payment button inside GroupContactsDrawer, the mousedown event bubbled up
           * all the way to row's onMouseDown handler which called onClose() - closing the drawer and the popover.
           * Added `onMouseDown={(e) => e.stopPropagation()}` on the `rightSlot` wrapper div.
           * This follows the exact same pattern already used by the component's own internal buttons (View Profile, More menu),
           * which already had explicit `e.stopPropagation()` on their `onMouseDown` to prevent the same issue.
           */
          <div css={{ flexShrink: 0 }} onMouseDown={(e) => e.stopPropagation()}>
            {rightSlot}
          </div>
        )}
        {children}
      </div>
    </NakedButton>
  );
};
